import { Alert, Card, Descriptions, List, Progress, Tag, Typography, notification } from 'antd';
import {
  CheckCircleTwoTone,
  FileTextOutlined,
  GlobalOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useEffect, useRef } from 'react';
import {
  isTerminalStatus,
  useBillingSummary,
  useIngestionStatus,
  useScrapeProgress,
  useSourcePages,
  useSources,
  type BillingSummary,
  type ScrapeProgress,
  type SourcePageItem,
  type SourceStatus,
} from '@entities/source';
import { DeleteSourceButton } from '@features/delete-source';

const { Title, Text } = Typography;

interface Props {
  assistantId: string;
  sourceId: string;
  onSourceGone: () => void;
}

/**
 * Детали выбранного источника.
 *
 * Что показывается:
 *  - Заголовок (имя, тип, дата) + кнопка удалить
 *  - Базовая инфа (статус, обработка картинок)
 *  - Для веб in_progress: карточка прогресса + список обработанных страниц
 *  - Для веб (любой статус): список страниц из БД (по мере commit'а)
 *  - Терминальные алерты (готово / частично / ошибка)
 *  - Блок «Стоимость обработки» (после vision_done)
 *
 * Toast «Источник обработан» показывается ОДИН РАЗ при переходе из
 * нетерминального статуса в success/partial_success. Используется ref
 * чтобы не сработало повторно при ре-рендерах.
 */
export function SourceDetailState({ assistantId, sourceId, onSourceGone }: Props) {
  const { data: sources, isLoading } = useSources(assistantId);
  const source = sources?.find((s) => s.source_id === sourceId);

  const isPolling =
    source?.status === 'in_queue' ||
    source?.status === 'in_progress' ||
    source?.status === 'pending_confirm';
  const { data: live } = useIngestionStatus(isPolling ? sourceId : undefined);

  const isWebsite = isWebsiteSource(source?.name ?? null);
  const effectiveStatus: SourceStatus | undefined = live?.status ?? source?.status;

  // Прогресс парсинга — только для веб-источников
  const shouldPollScrapeProgress = isWebsite && isPolling;
  const { data: scrapeProgress } = useScrapeProgress(
    shouldPollScrapeProgress ? sourceId : undefined,
  );

  // Список обработанных страниц — для веб-источников всегда (даже если success,
  // чтобы юзер видел финальный список). Поллинг автоматически останавливается
  // на терминальном статусе через useSourcePages.
  const { data: pagesData } = useSourcePages(
    isWebsite ? sourceId : undefined,
    effectiveStatus,
  );

  // Стоимость — только для терминальных
  const isTerminal = effectiveStatus ? isTerminalStatus(effectiveStatus) : false;
  const { data: billing } = useBillingSummary(sourceId, isTerminal);

  // Toast при завершении парсинга — показываем один раз
  const lastNotifiedStatusRef = useRef<SourceStatus | null>(null);
  useEffect(() => {
    if (!effectiveStatus || !source) return;
    if (lastNotifiedStatusRef.current === effectiveStatus) return;

    // Показываем toast только при переходе из нетерминального в success/partial.
    // Если юзер открыл уже готовый источник — не дёргаем (предыдущий ref === null,
    // новый статус сразу success, но это не переход — это первичный рендер).
    const previous = lastNotifiedStatusRef.current;
    lastNotifiedStatusRef.current = effectiveStatus;

    if (previous === null) return; // первая инициализация
    if (!isTerminalStatus(previous) && effectiveStatus === 'success') {
      notification.success({
        message: 'Источник обработан',
        description: isWebsite
          ? `Сайт ${source.name ?? 'успешно'} обработан и используется ассистентом при ответах.`
          : 'Документ обработан и используется ассистентом при ответах.',
        placement: 'topRight',
        duration: 6,
      });
    } else if (!isTerminalStatus(previous) && effectiveStatus === 'partial_success') {
      notification.warning({
        message: 'Источник обработан с замечаниями',
        description: 'Часть страниц не удалось обработать. Ассистент использует то, что есть.',
        placement: 'topRight',
        duration: 6,
      });
    } else if (!isTerminalStatus(previous) && effectiveStatus === 'fail') {
      notification.error({
        message: 'Не удалось обработать источник',
        description: 'Попробуйте загрузить снова или свяжитесь с поддержкой.',
        placement: 'topRight',
        duration: 8,
      });
    }
  }, [effectiveStatus, source, isWebsite]);

  // Если источник исчез — уведомляем родителя
  useEffect(() => {
    if (!isLoading && sources && !source) {
      onSourceGone();
    }
  }, [isLoading, sources, source, onSourceGone]);

  if (!source || !effectiveStatus) return null;

  return (
    <div style={{ padding: 24 }}>
      {/* Заголовок: иконка + имя + кнопка удалить */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <div style={{ flexShrink: 0, color: '#1677ff', fontSize: 24, marginTop: 2 }}>
          {isWebsite ? <GlobalOutlined /> : <FileTextOutlined />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Title
            level={4}
            style={{
              marginTop: 0,
              marginBottom: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={source.name ?? 'Без имени'}
          >
            {source.name ?? 'Без имени'}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {isWebsite ? 'Сайт' : 'Документ'} · добавлено {formatDate(source.created_at)}
          </Text>
        </div>
        <DeleteSourceButton
          sourceId={source.source_id}
          sourceName={source.name}
          assistantId={assistantId}
        />
      </div>

      {/* Базовая информация */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions
          column={1}
          size="small"
          labelStyle={{ fontSize: 12, color: '#8c8c8c', width: 140 }}
          contentStyle={{ fontSize: 13 }}
        >
          <Descriptions.Item label="Статус">
            <StatusTag status={effectiveStatus} />
          </Descriptions.Item>
          <Descriptions.Item label="Обработка картинок">
            {source.process_images ? 'Включена' : 'Выключена'}
          </Descriptions.Item>
          {source.confirm_expires_at && effectiveStatus === 'pending_confirm' && (
            <Descriptions.Item label="Подтвердить до">
              {formatDate(source.confirm_expires_at)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Прогресс для веб-источника в обработке */}
      {effectiveStatus === 'in_progress' && isWebsite && scrapeProgress && (
        <WebProgressCard progress={scrapeProgress} />
      )}

      {/* Базовый алерт для документов в обработке (без детального прогресса) */}
      {effectiveStatus === 'in_progress' && (!isWebsite || !scrapeProgress) && (
        <Alert
          type="info"
          showIcon
          message="Идёт обработка"
          description="Документ обрабатывается ассистентом. Это может занять несколько минут. Можно закрыть страницу и вернуться чуть позже."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Список обработанных страниц — для веб-источников всегда */}
      {isWebsite && pagesData && pagesData.pages.length > 0 && (
        <PagesListCard
          pages={pagesData.pages}
          totalProcessed={pagesData.total_processed}
          isInProgress={effectiveStatus === 'in_progress' || effectiveStatus === 'in_queue'}
        />
      )}

      {/* Терминальные алерты */}
      {effectiveStatus === 'partial_success' && (
        <Alert
          type="warning"
          showIcon
          message="Обработка завершена с замечаниями"
          description="Часть страниц не удалось обработать. Ассистент уже использует то, что обработалось успешно."
          style={{ marginBottom: 16 }}
        />
      )}

      {effectiveStatus === 'fail' && (
        <Alert
          type="error"
          showIcon
          message="Ошибка обработки"
          description="Не удалось обработать источник. Попробуйте загрузить снова или свяжитесь с поддержкой."
          style={{ marginBottom: 16 }}
        />
      )}

      {effectiveStatus === 'success' && (
        <Alert
          type="success"
          showIcon
          message="Готово"
          description="Источник успешно обработан и используется ассистентом при ответах."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Блок «Стоимость обработки» — только для терминальных и если есть данные */}
      {isTerminal && billing && billing.items.length > 0 && (
        <BillingCard billing={billing} />
      )}
    </div>
  );
}

// ===========================================================================
//  Прогресс веб-источника в деталях
// ===========================================================================

function WebProgressCard({ progress }: { progress: ScrapeProgress }) {
  const eta = formatEta(progress.eta_seconds);
  const isVisionPhase =
    progress.progress_status === 'done' &&
    progress.vision_status === 'vision_processing';

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>
          Парсинг страниц
        </Text>
        <Progress
          percent={progress.pages_percent}
          status={progress.progress_status === 'done' ? 'success' : 'active'}
          format={() => `${progress.pages_done} / ${progress.pages_total}`}
          style={{ marginTop: 8 }}
        />
      </div>

      {progress.images_done > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 14 }}>
            Обработка картинок
          </Text>
          <div style={{ marginTop: 6 }}>
            <Text>
              Обработано: <Text strong>{progress.images_done}</Text>
            </Text>
            {progress.images_kept > 0 &&
              progress.images_kept !== progress.images_done && (
                <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                  (с описанием: {progress.images_kept})
                </Text>
              )}
          </div>
          {isVisionPhase && (
            <Progress
              percent={100}
              size="small"
              status="active"
              showInfo={false}
              style={{ marginTop: 4 }}
            />
          )}
        </div>
      )}

      {eta && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Осталось примерно: {eta}
        </Text>
      )}

      {!eta && progress.progress_status !== 'done' && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Можно закрыть страницу — обработка продолжится в фоне.
        </Text>
      )}
    </Card>
  );
}

// ===========================================================================
//  Список обработанных страниц
// ===========================================================================

interface PagesListCardProps {
  pages: SourcePageItem[];
  totalProcessed: number;
  /** В процессе ли источник — для заголовка карточки и поведения. */
  isInProgress: boolean;
}

/**
 * Карточка со списком обработанных страниц веб-источника.
 *
 * Список приходит из GET /knowledge/sources/{id}/pages — только страницы
 * у которых уже есть чанки в БД. Появляются по мере обработки парсером.
 *
 * Каждая страница — одна строка с галочкой (✓) и URL. URL кликабельный
 * (открывается в новой вкладке) — юзер может сразу посмотреть что именно
 * было распарсено.
 */
function PagesListCard({ pages, totalProcessed, isInProgress }: PagesListCardProps) {
  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isInProgress ? (
            <LoadingOutlined style={{ color: '#1677ff' }} spin />
          ) : (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          )}
          <Text strong style={{ fontSize: 14 }}>
            Обработано страниц: {totalProcessed}
          </Text>
        </div>
      }
      style={{ marginBottom: 16 }}
      styles={{
        body: {
          maxHeight: 360,
          overflowY: 'auto',
          padding: 0,
        },
      }}
    >
      <List
        size="small"
        dataSource={pages}
        renderItem={(page) => (
          <List.Item style={{ padding: '8px 16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                minWidth: 0,
              }}
            >
              <CheckCircleTwoTone
                twoToneColor="#52c41a"
                style={{ flexShrink: 0, fontSize: 14 }}
              />
              <a
                href={page.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 13,
                  color: '#1A1A1A',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
                title={page.url}
              >
                {trimUrlPrefix(page.url)}
              </a>
              {page.chunks_count > 1 && (
                <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                  {page.chunks_count} чанков
                </Text>
              )}
            </div>
          </List.Item>
        )}
      />
    </Card>
  );
}

// ===========================================================================
//  Блок «Стоимость обработки»
// ===========================================================================

function BillingCard({ billing }: { billing: BillingSummary }) {
  return (
    <Card
      size="small"
      title={
        <Text strong style={{ fontSize: 14 }}>
          Стоимость обработки
        </Text>
      }
      styles={{ body: { padding: 16 } }}
    >
      {billing.items.map((item) => (
        <div
          key={item.phase}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '4px 0',
            fontSize: 13,
          }}
        >
          <Text>
            {item.label}{' '}
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({item.unit_count} {item.unit_label} × {formatRub(item.unit_price_rub)} ₽)
            </Text>
          </Text>
          <Text>{formatRub(item.amount_rub)} ₽</Text>
        </div>
      ))}

      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          marginTop: 8,
          paddingTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Text strong>Итого списано</Text>
        <Text strong style={{ fontSize: 15 }}>
          {formatRub(billing.total_rub)} ₽
        </Text>
      </div>
    </Card>
  );
}

// ===========================================================================
//  Helpers
// ===========================================================================

function isWebsiteSource(name: string | null): boolean {
  if (!name) return false;
  return /^https?:\/\//i.test(name);
}

/** Убирает https:// для красоты, оставляет домен + путь. */
function trimUrlPrefix(url: string): string {
  return url.replace(/^https?:\/\//i, '');
}

interface StatusTagProps {
  status: SourceStatus;
}

function StatusTag({ status }: StatusTagProps) {
  const config = STATUS_CONFIG[status];
  return (
    <Tag color={config.color} bordered={false} style={{ fontSize: 12, margin: 0 }}>
      {config.label}
    </Tag>
  );
}

const STATUS_CONFIG: Record<SourceStatus, { label: string; color: string }> = {
  pending_confirm: { label: 'Ждёт подтверждения', color: 'default' },
  in_queue: { label: 'В очереди', color: 'default' },
  in_progress: { label: 'Обработка...', color: 'blue' },
  success: { label: 'Готов', color: 'success' },
  partial_success: { label: 'Частично', color: 'warning' },
  fail: { label: 'Ошибка', color: 'error' },
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRub(value: string): string {
  return parseFloat(value).toFixed(2);
}

function formatEta(seconds: number | null): string | null {
  if (seconds === null || seconds < 60) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  if (restMin === 0) return `~${hours} ч`;
  return `~${hours} ч ${restMin} мин`;
}
